import { getBundleId } from '@wix/editor-elements-conventions';
import { isJS, isCSS, getExtension } from '../utils';
function getDepsURLs(indexes, shared = []) {
    return indexes.map(index => shared[index]);
}
export function createManifestAPI(manifest) {
    const buildComponentId = (componentName, part) => {
        return getBundleId({
            namespace: manifest.namespace,
            host: manifest.host,
            componentName,
            part,
        });
    };
    const buildHostId = () => {
        return getBundleId({
            namespace: manifest.namespace,
            host: manifest.host,
        });
    };
    const buildBatchId = (batch) => {
        return getBundleId({
            namespace: manifest.namespace,
            host: manifest.host,
            batch,
        });
    };
    const withBaseURL = (url) => {
        return `${manifest.baseURL}${url}`;
    };
    function extractModelSources(urls, { componentName, part, batch, } = {}) {
        const model = {};
        const prefix = getBundleId({
            namespace: manifest.namespace,
            host: manifest.host,
            componentName,
            part,
            batch,
        });
        urls.forEach(url => {
            if (Array.isArray(url)) {
                model.deps = getDepsURLs(url, manifest.shared).map(withBaseURL);
            }
            else {
                if (isJS(url)) {
                    model.js = withBaseURL(`${prefix}.${url}`);
                }
                else if (isCSS(url)) {
                    model.css = withBaseURL(`${prefix}.${url}`);
                }
            }
        });
        return model;
    }
    return {
        getManifest() {
            return manifest;
        },
        getEnvironment() {
            return {
                hot: manifest.environment?.hot
                    ? withBaseURL(`hot.${manifest.environment.hot}`)
                    : undefined,
                runtime: manifest.environment?.runtime
                    ? withBaseURL(`webpack-runtime.${manifest.environment.runtime}`)
                    : undefined,
            };
        },
        getNamespace() {
            return manifest.namespace;
        },
        getBaseUrl() {
            return manifest.baseURL;
        },
        getStatics(componentName) {
            const statics = manifest.statics ?? {};
            return {
                ...(manifest.libraryStatics || {}),
                ...(componentName ? statics[componentName] : {}),
            };
        },
        getLibraryStatics() {
            return manifest.libraryStatics ?? {};
        },
        getHostBundleModel() {
            return {
                name: manifest.host,
                id: buildHostId(),
                src: extractModelSources(manifest.model || []),
            };
        },
        getLibraryAssets() {
            return (manifest.assets?.map(([type, url]) => {
                return {
                    url: withBaseURL(url),
                    type,
                    extension: getExtension(url),
                };
            }) ?? []);
        },
        getParts() {
            const parts = {};
            Object.keys(manifest.parts ?? {}).forEach(componentName => {
                parts[componentName] = {};
                Object.keys(manifest.parts[componentName]).forEach(part => {
                    const urls = manifest.parts[componentName][part];
                    parts[componentName][part] = {
                        id: buildComponentId(componentName, part),
                        src: extractModelSources(urls, {
                            componentName,
                            part,
                        }),
                    };
                });
            });
            Object.entries(manifest.batches ?? {}).forEach(([batchId, batch]) => {
                const isBundlesConcatenated = batch.url_v2 && batch.url_v2.length;
                const url = (Object.keys(batch.url_v2 || []).length ? batch.url_v2 : batch.url);
                const src = extractModelSources(url, {
                    part: isBundlesConcatenated ? batchId : undefined,
                    batch: !isBundlesConcatenated ? batchId : undefined,
                });
                batch.parts?.forEach(([part, ...components]) => {
                    components.forEach(name => {
                        if (!parts[name]) {
                            parts[name] = {};
                        }
                        parts[name][part] = {
                            id: buildBatchId(batchId),
                            src,
                            batched: true,
                        };
                    });
                });
                batch.components?.forEach(name => {
                    batch.parts?.forEach(([part]) => {
                        if (!parts[name]) {
                            parts[name] = {};
                        }
                        parts[name][part] = {
                            id: buildBatchId(batchId),
                            src,
                            batched: true,
                        };
                    });
                });
            });
            return parts;
        },
        getComponents() {
            const components = {};
            Object.keys(manifest.components ?? {}).forEach(componentName => {
                const urls = manifest.components[componentName];
                components[componentName] = {
                    id: buildComponentId(componentName),
                    name: componentName,
                    src: extractModelSources(urls, { componentName }),
                };
            });
            Object.entries(manifest.batches ?? {}).forEach(([batchId, batch]) => {
                const isBundlesConcatenated = batch.url_v2 && batch.url_v2.length;
                const url = (Object.keys(batch.url_v2 || []).length ? batch.url_v2 : batch.url);
                const src = extractModelSources(url, {
                    part: isBundlesConcatenated ? batchId : undefined,
                    batch: !isBundlesConcatenated ? batchId : undefined,
                });
                batch.components?.forEach(componentName => {
                    components[componentName] = {
                        id: buildBatchId(batchId),
                        name: componentName,
                        src,
                    };
                });
            });
            Object.entries(manifest.batches ?? {}).forEach(([, batch]) => {
                batch.parts?.forEach(([, ...batchedPartComponents]) => {
                    batchedPartComponents.forEach(componentName => {
                        if (!components[componentName]) {
                            components[componentName] = {
                                id: null,
                                name: componentName,
                                src: {},
                            };
                        }
                    });
                });
            });
            return components;
        },
    };
}
//# sourceMappingURL=createManifestAPI.js.map